import { Search, MapPin, Sparkles, Hash, X } from "lucide-react";

const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin",
  WY: "Wyoming",
};

interface BrowseFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  city: string;
  onCityChange: (val: string) => void;
  cities: string[];
  state: string;
  onStateChange: (val: string) => void;
  states: string[];
  zip: string;
  onZipChange: (val: string) => void;
  service: string;
  onServiceChange: (val: string) => void;
  services: string[];
  onReset: () => void;
}

export default function BrowseFilters({
  search,
  onSearchChange,
  city,
  onCityChange,
  cities,
  state,
  onStateChange,
  states,
  zip,
  onZipChange,
  service,
  onServiceChange,
  services,
  onReset,
}: BrowseFiltersProps) {
  const hasFilters = search || city || state || service || zip;

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          id="browse-search"
          name="browse-search"
          type="text"
          placeholder="Search by name..."
          autoComplete="off"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
        />
        {hasFilters && (
          <button
            onClick={onReset}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-medium flex items-center gap-1"
            aria-label="Clear all filters"
          >
            <X className="w-3.5 h-3.5" />
            Reset
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* State filter */}
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <select
            id="browse-state"
            name="browse-state"
            value={state}
            onChange={(e) => onStateChange(e.target.value)}
            autoComplete="off"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow cursor-pointer"
          >
            <option value="">All states</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {STATE_NAMES[s] || s}
              </option>
            ))}
          </select>
        </div>

        {/* Zip code */}
        <div className="relative">
          <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            id="browse-zip"
            name="browse-zip"
            type="text"
            inputMode="numeric"
            placeholder="All zip codes"
            autoComplete="off"
            value={zip}
            onChange={(e) => onZipChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm placeholder:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow cursor-pointer"
          />
        </div>

        {/* City filter */}
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <select
            id="browse-city"
            name="browse-city"
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow cursor-pointer"
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Service filter */}
        <div className="relative">
          <Sparkles className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <select
            id="browse-service"
            name="browse-service"
            value={service}
            onChange={(e) => onServiceChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow cursor-pointer"
          >
            <option value="">All services</option>
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

    </div>
  );
}
